<template>
  <div class="verify-page">
    <MainNavbar />

    <section class="verify-wrapper">
      <div class="verify-card">

        <h1 class="title">Verify Your Email</h1>
        <p class="subtitle">
          We've sent a <strong>6-digit verification code</strong> to <strong>{{ email }}</strong>.
        </p>

        <!-- Error Display Section -->
        <div v-if="showError" class="error-section" :class="{ 'shake': errorShake }">
          <div class="error-icon">
            <i class="fa-solid fa-circle-exclamation"></i>
          </div>
          <div class="error-content" id="error-message">
            <h4 class="error-title">{{ errorTitle }}</h4>
            <p class="error-message">{{ errorMessage }}</p>
            <p v-if="attemptsLeft > 0" class="attempts-left" role="status" aria-live="polite">
              {{ attemptsLeft }} attempt{{ attemptsLeft !== 1 ? 's' : '' }} remaining
            </p>
          </div>
          <button class="error-close" @click="clearError">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <form @submit.prevent="verifyCode" class="verify-form" v-if="!verified">
          <div class="form-group">
            <label for="otp">Enter Verification Code</label>
            <div class="otp-container">
              <input
                id="otp"
                v-model="otp"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="6"
                placeholder="123456"
                class="otp-input"
                :class="{ 'error-input': showError }"
                :disabled="loading || verified"
                :aria-label="'Enter 6-digit verification code sent to ' + email"
                :aria-invalid="showError"
                :aria-describedby="showError ? 'error-message' : 'otp-hint'"
                required
                autocomplete="one-time-code"
                @input="onOtpInput"
                @keydown="onOtpKeydown"
                ref="otpInput"
              />
              <div id="otp-hint" class="otp-hint" v-if="otp.length > 0 && !showError">
                {{ 6 - otp.length }} character{{ (6 - otp.length) !== 1 ? 's' : '' }} remaining
              </div>
            </div>
          </div>

          <button class="submit-btn" type="submit" :disabled="loading || showError">
            <template v-if="!loading">
              <i class="fa-solid fa-check"></i>
              Verify Code
            </template>
            <template v-else>
              <i class="fa-solid fa-spinner fa-spin"></i>
              Verifying...
            </template>
          </button>

          <div class="action-links">
            <button 
              type="button"
              class="resend" 
              @click="resendCode"
              :disabled="resending || loading"
              :aria-label="resending ? 'Resending code...' : 'Resend verification code'"
            >
              <i class="fa-solid fa-paper-plane" :class="{ 'fa-spin': resending }"></i>
              <span v-if="!resending">Didn't receive a code? <span class="resend-text">Resend</span></span>
              <span v-else>Resending code...</span>
            </button>
            <button 
              type="button"
              class="back-link" 
              @click="goBack"
              :disabled="loading"
              aria-label="Go back to signup page"
            >
              <i class="fa-solid fa-arrow-left"></i>
              Use different email
            </button>
          </div>
        </form>

        <!-- ✅ SUCCESS MESSAGE -->
        <transition name="fade">
          <div v-if="verified" class="success-section">
            <div class="tick-wrapper">
              <svg class="tick" viewBox="0 0 52 52">
                <circle class="tick-circle" cx="26" cy="26" r="25" fill="none" />
                <path class="tick-check" fill="none" d="M14 27l7 7 16-16" />
              </svg>
            </div>

            <h2 class="success-text">Account Successfully Created!</h2>
            <p class="success-subtext">
              Welcome to IpromiseAfrica! You're signed in and ready to go.
            </p>

            <div class="success-actions">
              <router-link to="/ipromiseafrica/store" class="explore-link">
                <i class="fa-solid fa-store"></i>
                Start Shopping
              </router-link>
            </div>
          </div>
        </transition>

      </div>
    </section>

    <ThirdpartyFooter />
  </div>
</template>

<script>
/**
 * VerifyEmail Component
 * 
 * Email verification page for new user account creation.
 * Displays OTP input form, handles verification, resend functionality,
 * and shows success state upon successful verification.
 * 
 * Features:
 * - 6-digit OTP input with auto-submit
 * - Real-time validation and error handling
 * - Resend OTP functionality with rate limiting
 * - Attempt tracking and user feedback
 * - Responsive design for all screen sizes
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Smooth animations and transitions
 * 
 * @component
 * @name VerifyEmail
 * 
 * Data Flow:
 * 1. User arrives from signup page with email in query params
 * 2. User enters 6-digit OTP code
 * 3. Code is validated client-side, then sent to backend
 * 4. Backend verifies OTP and creates user account
 * 5. Success state shown with link to store; footer matches team SignUp (ThirdpartyFooter)
 * 
 * API Endpoints:
 * - POST /auth/verify-email - Verify OTP and create account
 * - POST /auth/resend-otp - Resend verification code
 */

import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "../thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "../../authen/auth";

export default {
  name: "VerifyEmail",
  components: {
    MainNavbar,
    ThirdpartyFooter,
  },

  data() {
    return {
      /**
       * OTP code entered by user (6 digits)
       * @type {string}
       */
      otp: "",
      
      /**
       * User's email address from query params
       * @type {string}
       */
      email: this.$route.query.email || "",
      
      /**
       * Loading state for API requests
       * @type {boolean}
       */
      loading: false,
      
      /**
       * Whether verification was successful
       * @type {boolean}
       */
      verified: false,
      
      /**
       * Whether to show error message
       * @type {boolean}
       */
      showError: false,
      
      /**
       * Whether to apply shake animation to error
       * @type {boolean}
       */
      errorShake: false,
      
      /**
       * Error message title
       * @type {string}
       */
      errorTitle: "",
      
      /**
       * Error message content
       * @type {string}
       */
      errorMessage: "",
      
      /**
       * Number of verification attempts remaining
       * @type {number}
       */
      attemptsLeft: 3,
      
      /**
       * Timeout ID for auto-clearing error messages
       * @type {number|null}
       */
      errorTimeout: null,
      
      /**
       * Whether resend is in progress
       * @type {boolean}
       */
      resending: false
    };
  },

  /**
   * Watchers for reactive data
   */
  watch: {
    /**
     * Clear error when user starts typing new OTP
     * @param {string} newVal - New OTP value
     */
    otp(newVal) {
      if (newVal && this.showError) {
        this.clearError();
      }
    },
    
    /**
     * Redirect if email is missing
     * @param {string} newVal - New email value
     */
    email(newVal) {
      if (!newVal) {
        this.$router.push({ name: 'CustomerSignup' });
      }
    }
  },

  /**
   * Component lifecycle hooks
   */
  mounted() {
    // Validate email presence
    if (!this.email) {
      this.showErrorMessage(
        "Email Required",
        "Please start the signup process to receive a verification code."
      );
      setTimeout(() => {
        this.$router.push({ name: 'CustomerSignup' });
      }, 2000);
      return;
    }
    
    // Focus OTP input on mount for better UX
    this.$nextTick(() => {
      this.$refs.otpInput?.focus();
    });
  },
  
  /**
   * Cleanup timeouts on component destroy
   */
  beforeUnmount() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  },

  methods: {
    /**
     * Handle OTP input changes
     * Removes non-digit characters and auto-submits when 6 digits are entered
     * 
     * @param {Event} event - Input event
     */
    onOtpInput(event) {
      // Remove non-digit characters
      this.otp = event.target.value.replace(/\D/g, '');
      
      // Auto-submit if 6 digits entered (better UX)
      if (this.otp.length === 6 && !this.loading) {
        this.verifyCode();
      }
    },

    /**
     * Handle keyboard input for OTP field
     * Only allows numeric input and standard navigation keys
     * 
     * @param {KeyboardEvent} event - Keyboard event
     */
    onOtpKeydown(event) {
      // Allow: backspace, delete, tab, escape, enter
      if ([46, 8, 9, 27, 13].includes(event.keyCode) ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode)) ||
          // Allow: home, end, left, right
          (event.keyCode >= 35 && event.keyCode <= 39)) {
        return;
      }
      
      // Ensure it's a number (both number pad and top row)
      if ((event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    },

    /**
     * Display error message with shake animation
     * 
     * @param {string} title - Error title
     * @param {string} message - Error message
     */
    showErrorMessage(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showError = true;
      this.errorShake = true;
      
      // Auto-clear error after 5 seconds
      if (this.errorTimeout) clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        this.clearError();
      }, 5000);
      
      // Remove shake animation after it completes
      setTimeout(() => {
        this.errorShake = false;
      }, 500);
      
      // Focus back on OTP input for better UX
      this.$nextTick(() => {
        this.$refs.otpInput?.focus();
        this.$refs.otpInput?.select();
      });
    },

    /**
     * Clear error message and reset error state
     */
    clearError() {
      this.showError = false;
      this.errorShake = false;
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
    },

    /**
     * Verify OTP code with backend
     * Performs client-side validation, then sends to API
     */
    async verifyCode() {
      // Client-side validation
      if (!this.otp) {
        this.showErrorMessage(
          "Empty Code",
          "Please enter the 6-digit verification code."
        );
        return;
      }

      if (this.otp.length !== 6) {
        this.showErrorMessage(
          "Invalid Code Length",
          "Verification code must be exactly 6 digits."
        );
        return;
      }

      if (!/^\d{6}$/.test(this.otp)) {
        this.showErrorMessage(
          "Invalid Characters",
          "Verification code must contain only numbers."
        );
        return;
      }

      // Prevent multiple simultaneous requests
      if (this.loading) return;

      this.loading = true;
      
      try {
        const response = await fetch(buildApiUrl("/auth/verify-email"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            email: this.email, 
            otp: this.otp 
          })
        });

        const data = await response.json();

        if (!response.ok) {
          // Decrement attempts on error
          if (this.attemptsLeft > 0) {
            this.attemptsLeft--;
          }
          
          // Check for specific error types
          if (response.status === 400) {
            if (data.detail?.includes("expired") || data.detail?.includes("Expired")) {
              this.showErrorMessage(
                "Code Expired",
                "This verification code has expired. Please request a new one."
              );
            } else if (data.detail?.includes("Invalid") || data.detail?.includes("incorrect")) {
              this.showErrorMessage(
                "Incorrect Code",
                `The code you entered is incorrect. ${this.attemptsLeft > 0 ? `${this.attemptsLeft} attempt${this.attemptsLeft !== 1 ? 's' : ''} remaining.` : ''}`
              );
            } else {
              this.showErrorMessage(
                "Verification Failed",
                data.detail || "Invalid verification code. Please try again."
              );
            }
          } else if (response.status === 404) {
            this.showErrorMessage(
              "Session Expired",
              "Your verification session has expired. Please start the signup process again."
            );
          } else if (response.status === 429) {
            this.showErrorMessage(
              "Too Many Attempts",
              "Too many failed attempts. Please wait 5 minutes before trying again."
            );
          } else {
            this.showErrorMessage(
              "Verification Error",
              data.detail || "Unable to verify your code. Please try again."
            );
          }
          
          // Clear OTP on error
          this.otp = "";
          return;
        }

        // Success — auto-login with the same token payload as login-verify
        const authStore = useAuthStore();
        if (data.access_token) {
          authStore.setAuthData(data.access_token, data.user);
        }

        this.loading = false;
        this.verified = true;
        this.clearError();

      } catch (error) {
        this.loading = false;
        this.showErrorMessage(
          "Connection Error",
          "Unable to connect to the server. Please check your internet connection and try again."
        );
        console.error("Verification Error:", error);
      }
    },

    /**
     * Resend OTP code to user's email
     * Includes rate limiting protection and proper error handling
     */
    async resendCode() {
      // Prevent multiple simultaneous requests
      if (this.resending || this.loading) return;
      
      this.resending = true;
      
      try {
        const response = await fetch(buildApiUrl("/auth/resend-otp"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: this.email }),
        });

        const data = await response.json();

        if (!response.ok) {
          // Handle rate limiting
          if (response.status === 429) {
            throw new Error("Too many requests. Please wait a few minutes before requesting a new code.");
          }
          throw new Error(data.detail || "Could not resend OTP");
        }

        // Show success message (using error display for consistency)
        this.showErrorMessage(
          "Code Resent",
          "A new verification code has been sent to your email."
        );
        
        // Reset attempts on successful resend
        this.attemptsLeft = 3;
        
        // Clear OTP field
        this.otp = "";
        
        // Focus input for better UX
        this.$nextTick(() => {
          this.$refs.otpInput?.focus();
        });

      } catch (error) {
        this.showErrorMessage(
          "Resend Failed",
          error.message || "Unable to resend verification code. Please try again."
        );
      } finally {
        this.resending = false;
      }
    },

    /**
     * Navigate back to signup page
     */
    goBack() {
      this.$router.push({ name: 'CustomerSignup' });
    }
  }
};
</script>

<style scoped>
.verify-page {
  width: 100%;
  min-height: 100vh;
  background: var(--background-color1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Same footer flush as team registration (SignUp.vue) */
.verify-page :deep(.thirdparty-footer) {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: stretch;
}

.verify-wrapper {
margin-top: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 2rem 1rem;
}

.verify-card {
  width: 100%;
  max-width: 450px;
  background: var(--background-color2);
  color: var(--text-color1);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 1rem;
  color: var(--text-color2);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

/* Error Section */
.error-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(255, 71, 87, 0.1), rgba(255, 107, 129, 0.05));
  border-left: 4px solid #ff4757;
  border-radius: 8px;
  text-align: left;
  animation: slideIn 0.3s ease;
}

.error-section.shake {
  animation: shake 0.5s ease;
}

.error-icon {
  width: 32px;
  height: 32px;
  background: #ff4757;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.error-icon i {
  color: white;
  font-size: 0.9rem;
}

.error-content {
  flex: 1;
}

.error-title {
  color: #ff4757;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.error-message {
  color: var(--text-color2);
  font-size: 0.9rem;
  margin-bottom: 4px;
  line-height: 1.4;
}

.attempts-left {
  color: #ffa502;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 4px;
}

.error-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  font-size: 1rem;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.error-close:hover {
  color: #666;
}

/* OTP Form */
.verify-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
}

.form-group {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-color2);
  font-weight: 500;
  text-align: left;
}

.otp-container {
  position: relative;
}

.otp-input {
  width: 100%;
  text-align: center;
  letter-spacing: 6px;
  font-size: 1.8rem;
  font-weight: 600;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid var(--border-color-hover1);
  background: var(--background-color3);
  color: var(--text-color1);
  outline: none;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
}

.otp-input:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-2px);
}

.otp-input.error-input {
  border-color: #ff4757;
  background: rgba(255, 71, 87, 0.05);
  animation: pulseError 2s infinite;
}

.otp-hint {
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  font-size: 0.8rem;
  color: var(--text-color2);
  text-align: center;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 16px;
  margin-top: 8px;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover1));
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(var(--border-color-hover-rgb), 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--border-color-hover1);
}

/* Action Links */
.action-links {
  width: 100%;
  margin-top: 20px;
}

.resend, .back-link {
  text-align: center;
  margin: 12px 0;
  color: var(--text-color2);
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: color 0.2s ease;
  background: none;
  border: none;
  padding: 8px;
  width: 100%;
  font-family: inherit;
}

.resend .resend-text {
  color: var(--border-color-hover);
  font-weight: 600;
}

.resend:hover:not(:disabled), .back-link:hover:not(:disabled) {
  color: var(--text-color1);
}

.resend:disabled, .back-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.resend i, .back-link i {
  font-size: 0.9rem;
}

/* Success Section */
.success-section {
  text-align: center;
  padding: 2rem 1rem;
  animation: fadeIn 0.8s ease;
}

.tick-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.tick {
  width: 80px;
  height: 80px;
  stroke: #4caf50;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  animation: scaleIn 0.8s ease forwards;
}

.tick-circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: drawCircle 1s ease forwards;
}

.tick-check {
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: drawCheck 0.6s ease forwards 0.9s;
}

.success-text {
  color: var(--text-color1);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.success-subtext {
  color: var(--text-color2);
  font-size: 1rem;
  margin-bottom: 2rem;
}

.success-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.explore-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  font-weight: 600;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover1));
  color: white;
}

.explore-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(var(--border-color-hover-rgb), 0.3);
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

@keyframes pulseError {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(255, 71, 87, 0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes scaleIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes drawCircle {
  to { stroke-dashoffset: 0; }
}

@keyframes drawCheck {
  to { stroke-dashoffset: 0; }
}

/* Smooth fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.6s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
/* Tablet and below (max-width: 768px) */
@media (max-width: 768px) {
  .verify-wrapper {
    padding: 1.5rem 1rem;
  }
  
  .verify-card {
    padding: 2rem 1.5rem;
    max-width: 100%;
  }
  
  .title {
    font-size: 1.6rem;
  }
  
  .subtitle {
    font-size: 0.95rem;
  }
  
  .otp-input {
    font-size: 1.6rem;
    padding: 15px;
    letter-spacing: 5px;
  }
  
  .success-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .explore-link {
    width: 100%;
    justify-content: center;
  }
}

/* Mobile (max-width: 600px) */
@media (max-width: 600px) {
  .verify-wrapper {
    padding: 1rem 0.75rem;
  }
  
  .verify-card {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }
  
  .title {
    font-size: 1.4rem;
    margin-bottom: 0.75rem;
  }
  
  .subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }
  
  .otp-input {
    font-size: 1.5rem;
    padding: 14px;
    letter-spacing: 4px;
  }
  
  .otp-hint {
    font-size: 0.75rem;
    bottom: -18px;
  }
  
  .submit-btn {
    padding: 14px;
    font-size: 1rem;
  }
  
  .success-section {
    padding: 1.5rem 0.5rem;
  }
  
  .success-text {
    font-size: 1.3rem;
  }
  
  .success-subtext {
    font-size: 0.95rem;
  }
  
  .success-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .explore-link {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.95rem;
  }

  .error-section {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    padding: 14px;
  }
  
  .error-icon {
    align-self: center;
    width: 28px;
    height: 28px;
  }
  
  .error-title {
    font-size: 0.95rem;
  }
  
  .error-message {
    font-size: 0.85rem;
  }
  
  .resend, .back-link {
    font-size: 0.9rem;
    padding: 10px;
  }
  
  .tick {
    width: 70px;
    height: 70px;
  }
}

/* Small Mobile (max-width: 480px) */
@media (max-width: 480px) {
  .verify-page {
    padding-top: 60px;
  }
  
  .verify-wrapper {
    padding: 0.75rem 0.5rem;
  }
  
  .verify-card {
    padding: 1.25rem 0.75rem;
  }
  
  .title {
    font-size: 1.3rem;
  }
  
  .subtitle {
    font-size: 0.85rem;
    line-height: 1.4;
  }
  
  .otp-input {
    font-size: 1.4rem;
    padding: 12px;
    letter-spacing: 3px;
  }
  
  .submit-btn {
    padding: 12px;
    font-size: 0.95rem;
  }
  
  .tick {
    width: 60px;
    height: 60px;
  }
  
  .success-text {
    font-size: 1.2rem;
  }
  
  .success-subtext {
    font-size: 0.9rem;
  }
}

/* Extra Small Mobile (max-width: 360px) */
@media (max-width: 360px) {
  .verify-card {
    padding: 1rem 0.5rem;
  }
  
  .title {
    font-size: 1.2rem;
  }
  
  .otp-input {
    font-size: 1.3rem;
    padding: 10px;
  }
  
  .error-section {
    padding: 12px;
  }
}

/* Landscape orientation on mobile */
@media (max-height: 600px) and (orientation: landscape) {
  .verify-wrapper {
    padding: 1rem;
  }
  
  .verify-card {
    padding: 1.5rem;
  }
  
  .title {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
  }
  
  .subtitle {
    margin-bottom: 1rem;
  }
  
  .success-section {
    padding: 1rem;
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .otp-input {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Print styles */
@media print {
  .verify-page {
    display: none;
  }
}
</style>